/**
 * Video with text
 * - Custom video control pill: play/pause the section video, with a label and
 *   a m:ss time read-out (duration when idle, elapsed while playing).
 * - Accordion steps: native <details>/<summary> with single-open behaviour
 *   (opening one step closes its siblings). Works without JS too.
 */
(function () {
  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '';
    var mins = Math.floor(seconds / 60);
    var secs = Math.floor(seconds % 60);
    return mins + ':' + (secs < 10 ? '0' + secs : secs);
  }

  function initVideoControl(section) {
    var pill = section.querySelector('[data-video-control]');
    var video = section.querySelector('.video-with-text__video');
    if (!pill || !video) return;

    var toggle = pill.querySelector('[data-video-toggle]');
    var timeEl = pill.querySelector('[data-video-time]');
    if (!toggle) return;

    function showDuration() {
      if (timeEl) timeEl.textContent = formatTime(video.duration);
    }

    // Metadata may already be loaded by the time JS runs.
    if (video.readyState >= 1) showDuration();
    video.addEventListener('loadedmetadata', showDuration);

    video.addEventListener('timeupdate', function () {
      if (timeEl && !video.paused) timeEl.textContent = formatTime(video.currentTime);
    });

    video.addEventListener('play', function () {
      pill.classList.add('is-playing');
      toggle.setAttribute('aria-pressed', 'true');
    });

    video.addEventListener('pause', function () {
      pill.classList.remove('is-playing');
      toggle.setAttribute('aria-pressed', 'false');
      showDuration();
    });

    video.addEventListener('ended', function () {
      pill.classList.remove('is-playing');
      toggle.setAttribute('aria-pressed', 'false');
      showDuration();
    });

    // Reflect the initial state (autoplay videos may already be playing).
    if (!video.paused && !video.ended) {
      pill.classList.add('is-playing');
      toggle.setAttribute('aria-pressed', 'true');
    }

    toggle.addEventListener('click', function () {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    });
  }

  function initAccordion(section) {
    var steps = section.querySelectorAll('.video-with-text__step');
    if (!steps.length) return;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var DURATION = 320;

    // Visibility is owned by the native <details> open state, so a closed step
    // is always fully hidden — only its question/summary shows. JS just slides
    // the answer's height by transitioning max-height between 0 and the exact
    // measured content height. We pin the start height and let the browser
    // paint it (rAF) BEFORE transitioning — that "paint first" step is what
    // makes opening from a hidden panel animate as smoothly as closing, instead
    // of snapping (a transition won't start in the same frame the panel becomes
    // visible).
    function slide(step, toOpen) {
      var content = step.querySelector('.video-with-text__step-content');
      if (!content) {
        step.open = toOpen;
        return;
      }

      // Cancel anything already in flight on this panel.
      if (content._vwtRaf) {
        cancelAnimationFrame(content._vwtRaf);
        content._vwtRaf = null;
      }
      if (content._vwtEnd) {
        content.removeEventListener('transitionend', content._vwtEnd);
        content._vwtEnd = null;
      }

      if (reduceMotion) {
        content.style.transition = '';
        content.style.maxHeight = '';
        step.open = toOpen;
        return;
      }

      var start = content.getBoundingClientRect().height; // 0 while hidden
      if (toOpen) step.open = true; // render so it can be measured and shown
      var end = toOpen ? content.scrollHeight : 0;

      // Pin the start height with no transition...
      content.style.transition = 'none';
      content.style.maxHeight = start + 'px';

      // ...paint that frame, then transition to the target on the next one.
      content._vwtRaf = requestAnimationFrame(function () {
        content._vwtRaf = requestAnimationFrame(function () {
          content._vwtRaf = null;
          content.style.transition = 'max-height ' + DURATION + 'ms ease';
          content.style.maxHeight = end + 'px';

          var onEnd = function (event) {
            if (event && event.propertyName !== 'max-height') return;
            content.removeEventListener('transitionend', onEnd);
            content._vwtEnd = null;
            content.style.transition = '';
            content.style.maxHeight = '';
            if (!toOpen) step.open = false; // native hide once fully collapsed
          };
          content._vwtEnd = onEnd;
          content.addEventListener('transitionend', onEnd);
        });
      });
    }

    steps.forEach(function (step) {
      var summary = step.querySelector('.video-with-text__step-summary');
      if (!summary) return;

      summary.addEventListener('click', function (event) {
        event.preventDefault();
        // Each step toggles independently — several can be open at once.
        slide(step, !step.open);
      });
    });
  }

  function initSection(section) {
    if (!section || section.dataset.vwtInit === 'true') return;
    section.dataset.vwtInit = 'true';
    initVideoControl(section);
    initAccordion(section);
  }

  function initAll() {
    document.querySelectorAll('.video-with-text[data-section-id]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-initialise when a section is re-rendered in isolation.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.video-with-text[data-section-id]');
    if (section) initSection(section);
  });
})();
